import React, { useState } from 'react'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Stack from '@mui/material/Stack'

import { H3, Icon, P, Text } from '@mxenabled/mxui'
import { ExpandMore } from '@mxenabled/mx-icons'

import { ANALYTICS_EVENTS, useEvent } from '../../common'
import { type HelpCategory, type SelectedTopic } from '../../core'

export interface HelpListProps {
  buttonText: string
  hasRequestButton?: boolean
  helpCategories: HelpCategory[]
  helpContent: string
  helpTitle: string
  onRequestHelp: () => void
  onTopicSelect: (topic: SelectedTopic) => void
}

export const HelpList: React.FC<HelpListProps> = ({
  buttonText,
  hasRequestButton = false,
  helpCategories = [],
  helpContent,
  helpTitle,
  onRequestHelp,
  onTopicSelect,
}) => {
  const [expanded, setExpanded] = useState<number | null>(null)
  const midpoint = Math.ceil(helpCategories.length / 2)

  const { onEvent } = useEvent()

  return (
    <React.Fragment>
      <Grid
        container={true}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 24,
        }}
      >
        <Stack>
          <H3>{helpTitle}</H3>
          <P variant="body1">{helpContent}</P>
        </Stack>
        {hasRequestButton && (
          <Stack
            sx={{
              pt: {
                xs: 12,
                md: 0,
              },
            }}
          >
            <Button
              aria-label={buttonText}
              onClick={() => {
                onEvent(ANALYTICS_EVENTS.HELP_CLICK_REQUEST_SUPPORT, {})

                onRequestHelp()
              }}
              variant="contained"
            >
              {buttonText}
            </Button>
          </Stack>
        )}
      </Grid>

      <Stack sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: 12 }}>
        {[0, 1].map((half) => {
          const start = half === 0 ? 0 : midpoint
          const end = half === 0 ? midpoint : helpCategories.length
          const items = helpCategories.slice(start, end)

          return (
            <Stack key={half} sx={{ flex: 1, gap: 12, maxWidth: { md: 'calc(50% - 6px)' } }}>
              {items.map(({ guid, icon, topics, title }, i) => {
                const key = i + start
                return (
                  <Accordion
                    disableGutters={true}
                    expanded={key === expanded}
                    key={guid}
                    onChange={(_, isExpanded) => {
                      onEvent(ANALYTICS_EVENTS.HELP_EXPAND_TOPIC, { feature: title })

                      setExpanded(isExpanded ? key : null)
                    }}
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.neutral?.light}`,
                      borderRadius: '8px !important',
                      '&:before': {
                        opacity: 0,
                      },
                      '& .MuiAccordion-region': {
                        bgcolor: 'background.default',
                        borderBottomLeftRadius: 8,
                        borderBottomRightRadius: 8,
                      },
                    })}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: 'text.primary' }} />}
                      sx={{ height: 64, p: '14px 24px' }}
                    >
                      <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 12 }}>
                        <Icon name={icon} size={24} />
                        <Text variant="body1">{title}</Text>
                      </Stack>
                    </AccordionSummary>
                    <Divider />
                    <AccordionDetails
                      sx={{
                        ml: 56,
                        p: 0,
                      }}
                    >
                      <List disablePadding={true}>
                        {topics.map(({ label, topicKey }) => (
                          <ListItemButton
                            aria-label={label}
                            divider={true}
                            key={topicKey}
                            onClick={() =>
                              onTopicSelect({ guid, category: title, pageTitle: label, topicKey })
                            }
                            sx={{
                              pl: '6px !important',
                              borderColor: 'neutral.light',
                              '&:last-child': {
                                border: 'none !important',
                                borderBottomRightRadius: 7,
                              },
                            }}
                          >
                            <Text color="secondary" variant="body2">
                              {label}
                            </Text>
                          </ListItemButton>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )
              })}
            </Stack>
          )
        })}
      </Stack>
    </React.Fragment>
  )
}
